import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "../../api";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "../ui/sonner";
import { INTEGRATIONS } from "../../constants/testIds";
import { SubmitButton } from "../common/SubmitButton";
import { useSuccessPulse } from "../../hooks/useSuccessPulse";

const FIELDS = [
  { key: "displayName", label: "Display name", placeholder: "e.g. FitFrik Mumbai WhatsApp", testId: INTEGRATIONS.connectDisplayNameInput },
  { key: "fromAddress", label: "WhatsApp business number", placeholder: "+91XXXXXXXXXX", testId: INTEGRATIONS.connectFromAddressInput },
  { key: "wabaId", label: "WhatsApp Business Account (WABA) ID", placeholder: "From Meta Business Manager", testId: INTEGRATIONS.connectWabaIdInput },
  { key: "phoneNumberId", label: "Phone Number ID", placeholder: "From Meta Business Manager", testId: INTEGRATIONS.connectPhoneNumberIdInput },
];

const SECRET_FIELDS = [
  { key: "accessToken", label: "Access token", testId: INTEGRATIONS.connectAccessTokenInput },
  { key: "appSecret", label: "App secret", testId: INTEGRATIONS.connectAppSecretInput },
];

const BLANK = { displayName: "", fromAddress: "", wabaId: "", phoneNumberId: "", accessToken: "", appSecret: "" };

// UX-CP-02 §2.1 / Prompt 13 SCR-P04-30 "Implementation Team connects a gym's provider" — the ONE
// place a gym's first `integration_config` is created WITH credentials already attached in a
// single call (`POST /control/gyms/{org}/integrations` — `secrets_service.cp_create_config`).
// Status lands `pending`, not `active` — the gym only shows as connected once the "Test" button
// (back on the console table) genuinely reaches Meta's Graph API and succeeds.
export function ConnectWhatsappDialog({ orgId, open, onOpenChange }) {
  const [form, setForm] = useState(BLANK);
  const queryClient = useQueryClient();
  const [success, pulse] = useSuccessPulse();

  const connectMutation = useMutation({
    mutationFn: () => api.post(`/control/gyms/${orgId}/integrations`, {
      channel: "whatsapp", provider: "meta_cloud_api", display_name: form.displayName, from_address: form.fromAddress,
      public_config: { waba_id: form.wabaId, phone_number_id: form.phoneNumberId },
      secret: { access_token: form.accessToken, app_secret: form.appSecret },
    }),
    onSuccess: () => pulse(() => {
      toast.success("WhatsApp connector created — click Test to verify it with Meta.");
      queryClient.invalidateQueries({ queryKey: ["gym-integrations", orgId] });
      setForm(BLANK);
      onOpenChange(false);
    }),
    onError: (e) => toast.error(formatApiError(e).message),
  });

  const canSubmit = [...FIELDS, ...SECRET_FIELDS].every((f) => form[f.key].trim());

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setForm(BLANK); }}>
      <DialogContent className="glass-modal" data-testid={INTEGRATIONS.connectWhatsappDialog}>
        <DialogHeader>
          <DialogTitle>Connect WhatsApp (Meta Cloud API)</DialogTitle>
          <DialogDescription>Enter this gym's own Meta WhatsApp Business Account credentials — every gym self-onboards its own WABA (Option A).</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {FIELDS.map((f) => (
            <div className="space-y-1.5" key={f.key}>
              <Label htmlFor={f.testId}>{f.label} <span style={{ color: "var(--scarlet-neg)" }}>*</span></Label>
              <Input id={f.testId} data-testid={f.testId} placeholder={f.placeholder} value={form[f.key]} onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))} />
            </div>
          ))}
          {SECRET_FIELDS.map((f) => (
            <div className="space-y-1.5" key={f.key}>
              <Label htmlFor={f.testId}>{f.label} <span style={{ color: "var(--scarlet-neg)" }}>*</span></Label>
              <Input id={f.testId} type="password" data-testid={f.testId} value={form[f.key]} onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))} />
            </div>
          ))}
        </div>
        <DialogFooter>
          <SubmitButton testId={INTEGRATIONS.connectSubmit} disabled={!canSubmit} pending={connectMutation.isPending} success={success} onClick={() => connectMutation.mutate()} label="Connect" successLabel="Connected" />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
